import React, { useCallback, memo } from "react";
import { useReportConfig, useReportFilters, useWidgetEditing } from "../../ReportLandingContext";
import { WidgetConfigFormRenderer } from "../WidgetConfigFormRenderer";
// import { ReportSettingForm } from "../ReportSettingForm";
import { StyledFilterHeader, StyledWidgetFormHeader } from "../../styled";
import { TabBarStyle2 } from "oolib/dist/v2/components/TabBar";
import { ButtonGhost, ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";
import { colors2, UI_BODY_SEMIBOLD_SM, ButtonGhostV2, icons } from "oolib";
import { FormRendererPlaceholder } from "../FormRendererPlaceholder";
import FilterModule from "@/components/discovery/FilterModule";
import { useWidgetFilterControls } from "../WidgetComps/utils/useWidgetFilterControls";
import { tabBarFormRendererOptions } from "../../CONFIG";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useParams } from "react-router-dom";

const { Funnel } = icons;

/**
 * FilterSidebar Component - Handles the filter module in read-only mode
 * Memoized to prevent unnecessary re-renders during sidebar toggle
 */
export const FilterSidebar = memo(({ filterConfig, setSidebarToggleState, reportId: reportIdProp, showSidebarCollapseButton, viewMode }) => {

  const { reportId } = useParams
  const { activeFilters, setActiveFilters } = useReportFilters();
  const allTpls = useGetQueryData('allTpls')
  const { sidebarFilterActionButtons, filterControlProps, showFilterModuleV3 } = useWidgetFilterControls();
  const handleClose = useCallback(() => {
    setSidebarToggleState({ lsb: false, rsb: false });
  }, [setSidebarToggleState]);

  return (
    <div>
      <StyledFilterHeader $compact={showFilterModuleV3} $viewMode={viewMode}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <Funnel size={16} weight="bold" color={colors2.grey80} />
          <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80, paddingTop: "0.1rem" }}>
            Filters
          </UI_BODY_SEMIBOLD_SM>
        </div>
        {showFilterModuleV3 ? (
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            {sidebarFilterActionButtons}
            {showSidebarCollapseButton && <ButtonGhostV2 icon="ArrowLineLeft" onClick={handleClose} />}
          </div>
        ) : (
          <div>
            <ButtonTertiaryCompact
              responsive={true}
              value={"Close"}
              onClick={handleClose}
            />
          </div>
        )}
      </StyledFilterHeader>

      <FilterModule
        usesAnnoFilters={true}
        filterConfig={filterConfig}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        usedIn={`report-readOnly-${reportIdProp ?? reportId}`}
        /**
         * below prop is needed, else authorstype filter won't work.
         * For now we can take the easy road and pass all the publishing
         * contentTypes thru, since authors type is a common to all
         * contenttypes filter in autogenfilters logic.
         */
        contentTypes={allTpls.filter(tpl => tpl.general.segment === 'publishing').map(d => d.kp_content_type)}
        {...filterControlProps}
      />
    </div>
  );
});



/**
 * WidgetFormSidebar Component - Handles the widget configuration forms
 * Memoized to prevent unnecessary re-renders during sidebar toggle
 */
export const WidgetFormSidebar = memo(({ 
  formRendererOption, 
  setFormRendererOption,
  sidebarToggleState, 
  setSidebarToggleState,
  data,
  localReportMeta,
  setLocalReportMeta,
  setShowDeleteModal,
  filterConfig
}) => {
  const { underEditWidgetIdx, underEditWidgetId } = useWidgetEditing();
  const { config, setConfig } = useReportConfig();
  const { activeFilters, setActiveFilters } = useReportFilters();

  const handleSidebarToggle = useCallback(() => {
    setSidebarToggleState(prev => ({ lsb: !prev.lsb, rsb: false }));
  }, [setSidebarToggleState]);

  const renderForm = () => {
    const hasWidgetSelected = underEditWidgetIdx !== undefined;

    switch(formRendererOption.value) {
      case "widget":
        return hasWidgetSelected ?
          (
            <WidgetConfigFormRenderer
              key={underEditWidgetId}
            />
          ) : <FormRendererPlaceholder />;

      // case "setting":
      //   return (
      //     <ReportSettingForm 
      //       data={data}
      //       localReportMeta={localReportMeta}
      //       setLocalReportMeta={setLocalReportMeta}
      //       setShowDeleteModal={setShowDeleteModal}
      //     />
      //   );
      
      case "filters":
        return (
          <FilterSidebar 
            filterConfig={filterConfig}
            isSidebarOpen={sidebarToggleState?.lsb}
            setSidebarToggleState={handleSidebarToggle}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <StyledWidgetFormHeader style={{ borderBottom: sidebarToggleState?.lsb && `1px solid ${colors2.grey10}` }}>
        <TabBarStyle2
          options={tabBarFormRendererOptions}
          onChange={(id, v) => setFormRendererOption(v)} 
          value={formRendererOption}
        />
        <ButtonGhost
          icon={sidebarToggleState?.lsb ? "ArrowLineLeft" : "ArrowLineRight"}
          onClick={handleSidebarToggle}
        />
      </StyledWidgetFormHeader>

      {sidebarToggleState?.lsb && renderForm()} 
    </div>
  );
});